/* © CNES */
import { Extent } from 'ol/extent';
import { createContext, useCallback, useMemo, useReducer } from 'react';
import { CollectionDetails } from '../models/CollectionDetails';
import { Feature } from '../models/Feature';
import { GeoJsonPolygon } from '../models/GeoJsonPolygon';
import {
  ItemAvailability,
  ItemsAvailabilities,
} from '../models/ItemAvailability';
import { ProductsAdded } from '../models/ProductsAdded';
import { UserLoggedIn } from '../models/UserLoggedIn';

/**
 * Global Context Type.
 */
export type ContextType = {
  isLeftSidePanelShown: boolean;
  setLeftPanelVisibility: Function;
  isRightSidePanelShown: boolean;
  setRightPanelVisibility: Function;
  setBothPanelVisibility: Function;
  userLoggedIn: UserLoggedIn;
  setUserLoggedIn: Function;
  productsAdded: ProductsAdded;
  setProductsAdded: Function;
  selectedProduct: CollectionDetails;
  queryPage: number;
  nextToken: string | null;
  setQueryPage: Function;
  setNextToken: Function;
  searchedFeatures: Feature[];
  featuresCount: number;
  setSearchedFeatures: Function;
  mapExtent: { extent: number[]; currentView: boolean; bbox: number[] };
  setMapExtent: Function;
  searchZone: { type: string | null; value: GeoJsonPolygon | null };
  setSearchZone: Function;
  isAPIRateLimitExceeded: boolean;
  setAPIRateLimitExceeded: Function;
  drawnShape: Extent | undefined;
  setDrawnShape: Function;
  setResetZoneTags: Function;
  dispatch: Function;
  isError400: boolean;
  setError400: Function;
  isError409: boolean;
  setError409: Function;
  isGetStarted: boolean;
  setIsGetStarted: Function;
  downloadingProducts: string[];
  setDownloadingProducts: Function;
  doNotShowAgain: boolean;
  setDoNotShowAgain: Function;
  selectedProductOrigin: string | null;
  itemsAvailabilities: ItemsAvailabilities | undefined;
  setItemsAvailabilities: Function;
};

/**
 * Context Initial Value
 */
export const configContextInitialValue = {
  isLeftSidePanelShown: true,
  isRightSidePanelShown: false,
  userLoggedIn: {
    name: '',
    email: '',
    token: { value: '', expiresInMS: 0 },
  } as UserLoggedIn,
  productsAdded: {
    products: [] as string[],
    results: [] as string[],
    levelSet: new Set<string>(),
    satelliteSet: new Set<string>(),
    collectionsDetails: [] as CollectionDetails[],
  } as ProductsAdded,
  selectedProduct: {} as CollectionDetails,
  queryPage: 1,
  nextToken: null,
  searchedFeatures: [] as Feature[],
  featuresCount: 0,
  mapExtent: {
    extent: [
      -20037508.342789244, -8465847.274828456, 20037508.342789244,
      8465847.274828456,
    ],
    currentView: true,
    bbox: [
      -20037508.342789244, -8465847.274828456, 20037508.342789244,
      8465847.274828456,
    ],
  },
  searchZone: {
    type: null as null | string,
    value: null as
      | null
      | {
          type: null | string;
          properties: {};
          geometry: { type: null | string; coordinates: number[][][] };
        }[],
  },
  isAPIRateLimitExceeded: false,
  isError400: false,
  isError409: false,
  drawnShape: undefined as undefined | number[],
  isGetStarted: false,
  downloadingProducts: [] as string[],
  doNotShowAgain: false,
  selectedProductOrigin: null,
  itemsAvailabilities: undefined,
};

/**
 * create the pages parameters context
 */
export const ConfigContext = createContext({} as ContextType);

/**
 * reducer to chain context set actions.
 * @param state current state of context.
 * @param action action to reduce.
 * @returns new state.
 */
function reducer(state: any, action: any) {
  return { ...state, ...action };
}
/**
 * Config Context Provider.
 * @param children Application components to access context.
 * @returns Element.
 */
export const ConfigContextProvider = ({ children }: { children: any }) => {
  const [state, dispatch] = useReducer(reducer, {
    ...configContextInitialValue,
  });

  /**
   * set LeftPanel Visibility.
   * @param isLeftSidePanelShown boolean
   */
  const setLeftPanelVisibility = useCallback(
    (isLeftSidePanelShown: boolean) => {
      dispatch({ isLeftSidePanelShown });
    },
    []
  );
  /**
   * set RightPanel Visibility.
   * @param isRightSidePanelShown boolean
   * @param selectedProduct CollectionDetails product to display in panel.
   */
  const setRightPanelVisibility = useCallback(
    (
      isRightSidePanelShown: boolean,
      selectedProductInResults?: CollectionDetails,
      selectedProductOrigin?: string
    ) => {
      const selectedProduct = selectedProductInResults ?? state.selectedProduct;
      dispatch({
        isRightSidePanelShown,
        selectedProduct,
        selectedProductOrigin,
      });
    },
    [state.selectedProduct]
  );
  /**
   * set User LoggedIn
   * @param userLoggedIn UserLoggedIn
   */
  const setUserLoggedIn = useCallback((userLoggedIn: UserLoggedIn) => {
    dispatch({ userLoggedIn });
  }, []);
  /**
   * set Products Added
   * @param productsAdded ProductsAdded
   */
  const setProductsAdded = useCallback((productsAdded: ProductsAdded) => {
    dispatch({ productsAdded });
  }, []);
  /**
   * set Query Page
   * @param queryPage number
   */
  const setQueryPage = useCallback((queryPage: number) => {
    dispatch({ queryPage });
  }, []);

  const setNextToken = useCallback((nextToken: string) => {
    dispatch({ nextToken });
  }, []);
  /**
   * set earched Features
   * @param searchedFeatures Feature[]
   */
  const setSearchedFeatures = useCallback(
    (searchedFeatures: Feature[], featuresCount: number) => {
      dispatch({ searchedFeatures, featuresCount });
    },
    []
  );
  /**
   * set Map Extent
   * @param mapExtent
   * extent: used in REGARDS,
   * currentView: if user wants Extent from his perspective (or whole world if false)
   * bbox: used in GEOSERVER to display tiles with a scaling factor from Extent
   */
  const setMapExtent = useCallback(
    (mapExtent: { extent: number[]; currentView: boolean; bbox: number[] }) => {
      dispatch({ mapExtent });
    },
    []
  );

  /**
   * set geoJson file
   * @param searchZone geoJson | null
   */
  const setSearchZone = useCallback((searchZone: GeoJsonPolygon | null) => {
    dispatch({
      ...{ searchZone, drawnShape: undefined },
    });
  }, []);

  /**
   * set the visibility of the right and left panels.
   *  @param isLeftSidePanelShown boolean
   *  @param isRightSidePanelShown boolean
   */
  const setBothPanelVisibility = useCallback(
    (isLeftSidePanelShown: boolean, isRightSidePanelShown: boolean) => {
      dispatch({ isLeftSidePanelShown, isRightSidePanelShown });
    },
    []
  );

  /**
   * set API Rate Limi tExceeded.
   * @param isAPIRateLimitExceeded boolean
   */
  const setAPIRateLimitExceeded = useCallback(
    (isAPIRateLimitExceeded: boolean) => {
      dispatch({ isAPIRateLimitExceeded });
    },
    []
  );

  /**
   * set Error 400
   * @param isError400 boolean
   */
  const setError400 = useCallback((isError400: boolean) => {
    dispatch({ isError400 });
  }, []);

  /**
   * set Error 409
   * @param isError409 boolean
   */
  const setError409 = useCallback((isError409: boolean) => {
    dispatch({ isError409 });
  }, []);

  /**
   * set Drawn Shape
   * @param drawnShape Extent | undefined
   */
  const setDrawnShape = useCallback((drawnShape: Extent | undefined) => {
    dispatch({
      ...{ drawnShape, searchZone: { type: null, value: null } },
    });
  }, []);

  /**
   * set Reset Zone Tags.
   */
  const setResetZoneTags = useCallback(() => {
    dispatch({
      ...{
        drawnShape: undefined,
        searchZone: { type: null, value: null },
        mapExtent: {
          bbox: configContextInitialValue.mapExtent.bbox,
          extent: configContextInitialValue.mapExtent.extent,
          currentView: true,
        },
      },
    });
  }, []);

  /**
   * setDownloadingProducts
   * @param downloadProduct product beign downloaded.
   */
  const setDownloadingProducts = useCallback(
    (downloadProduct: string) => {
      let downloadingProducts: string[] = state.downloadingProducts;
      if (!downloadingProducts.includes(downloadProduct)) {
        downloadingProducts.push(downloadProduct);
      } else {
        downloadingProducts.splice(
          downloadingProducts.findIndex(
            (contextProduct) => contextProduct === downloadProduct
          ),
          1
        );
      }

      dispatch({
        ...{
          downloadingProducts,
        },
      });
    },
    [state.downloadingProducts]
  );
  /**
   * set isGetStarted value
   * @param isGetStarted boolean
   */
  const setIsGetStarted = useCallback((isGetStarted: boolean) => {
    dispatch({ isGetStarted });
  }, []);

  /**
   * set setDoNotShowAgain value
   * @param doNotShowAgain boolean
   */
  const setDoNotShowAgain = useCallback((doNotShowAgain: boolean) => {
    dispatch({ doNotShowAgain });
  }, []);

  /**
   * set itemsAvailabilities list
   * @param itemsAvailabilities
   */
  const setItemsAvailabilities = useCallback(
    (itemsAvailabilities: ItemAvailability) => {
      dispatch({ itemsAvailabilities });
    },
    []
  );

  /**
   * context Value
   */
  const contextValue = useMemo(() => {
    return {
      ...state,
      setLeftPanelVisibility,
      setRightPanelVisibility,
      setBothPanelVisibility,
      setUserLoggedIn,
      setProductsAdded,
      setQueryPage,
      setNextToken,
      setSearchedFeatures,
      setMapExtent,
      setSearchZone,
      setAPIRateLimitExceeded,
      setDrawnShape,
      setResetZoneTags,
      setError400,
      setError409,
      setIsGetStarted,
      setDownloadingProducts,
      setDoNotShowAgain,
      setItemsAvailabilities,
      dispatch,
    };
  }, [
    state,
    setLeftPanelVisibility,
    setRightPanelVisibility,
    setBothPanelVisibility,
    setUserLoggedIn,
    setProductsAdded,
    setQueryPage,
    setNextToken,
    setSearchedFeatures,
    setMapExtent,
    setSearchZone,
    setAPIRateLimitExceeded,
    setDrawnShape,
    setResetZoneTags,
    setError400,
    setError409,
    setIsGetStarted,
    setDownloadingProducts,
    setDoNotShowAgain,
    setItemsAvailabilities,
    dispatch,
  ]);
  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};
